import { Dispatch, SetStateAction } from "react";

/**
 * Shape of the `last_response` payload stored for a lead delivery attempt.
 * Consumed by `renderResponseMessage`, which reads `response.data`,
 * `response.message` and finally the root level `message`.
 */
export interface LeadResponseBody {
    code?: number | string;
    data?: unknown;
    message?: string;
}

export interface LeadLastResponse {
    response?: LeadResponseBody;
    message?: string;
}

export interface FailedLead {
    stat_id: number;
    lead_type: string;
    total_retries: number;
    last_retried_at: string | null;
    last_status: string;
    /** False when the lead can no longer be retried (retry limit reached). */
    retry_available: boolean;
    /** True while a retry is already queued on the backend. */
    scheduled: boolean;
    /** The API sends `[]` when no attempt has been recorded yet. */
    last_response?: LeadLastResponse | LeadLastResponse[] | null;
}

/**
 * `last_response` is either an object or an empty array, so narrow before
 * reading the nested response body.
 */
export const getLeadResponseBody = (
    lastResponse: FailedLead["last_response"],
): LeadResponseBody | undefined =>
    lastResponse && !Array.isArray(lastResponse)
        ? lastResponse.response
        : undefined;

export interface LeadsPagination {
    currentPage: number;
    pageSize: number;
    total: number;
    lastPage: number;
}

export type LeadsPaginationSetter = Dispatch<SetStateAction<LeadsPagination>>;

export type GetLeadsHandler = (
    page?: number,
    pageSize?: number,
) => void | Promise<void>;

/** Body accepted by `POST lead/connection/:id/failed-leads/retry`. */
export type RetryFailedLeadsPayload =
    | { stat_ids: number[] }
    | { all_stats: true };

export enum FailedLeadsTab {
    Failed = 0,
    Duplicate = 1,
}

export interface FailedLeadsModalProps {
    /** Destination type label shown on every lead card. */
    destination?: string;
    onClose: () => void;
    failedLeads: FailedLead[];
    duplicateLeads: FailedLead[];
    handleGetFailedLeads: GetLeadsHandler;
    handleGetDuplicateLeads: GetLeadsHandler;
    isLoadingFailedLeads: boolean;
    failedLeadsPagination: LeadsPagination;
    duplicateLeadsPagination: LeadsPagination;
    setFailedLeadsPagination: LeadsPaginationSetter;
    setDuplicateLeadsPagination: LeadsPaginationSetter;
    firstActiveTab?: FailedLeadsTab;
}
